/**
 * ABTestingTab - A/B testing management for automated campaigns
 */
import { FlaskConical, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { DISCOUNT_OPTIONS } from './automatedUtils';

export default function ABTestingTab({
  abTests,
  abTestResults,
  showCreateDialog,
  newABTest,
  onSetShowCreateDialog,
  onSetNewABTest,
  onCreateABTest,
  onViewResults,
  onClearResults
}) {
  return (
    <div className="space-y-6">
      {/* Create New Test */}
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-foreground">Campaign Variants</h3>
        <Button onClick={() => onSetShowCreateDialog(true)} data-testid="create-ab-test-btn">
          <FlaskConical className="w-4 h-4 mr-2" />
          Create A/B Test Variant
        </Button>
      </div>
      
      {/* Active Tests */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {abTests.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground bg-card rounded-xl border border-border">
            <FlaskConical className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
            <p>No A/B test variants created yet</p>
            <p className="text-sm">Create a variant to test different discount percentages</p>
          </div>
        ) : (
          abTests.map((test) => (
            <div key={test.variant_id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">{test.variant_name}</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  test.is_active ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500'
                }`}>
                  {test.is_active ? 'Active' : 'Paused'}
                </span>
              </div>
              <p className="text-2xl font-bold text-primary mb-1">{test.discount_percent}%</p>
              <p className="text-xs text-muted-foreground mb-3">Campaign {test.campaign_id}</p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{test.signups || 0} signups</span>
                <span>•</span>
                <span>{test.conversion_rate || 0}% conversion</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-3"
                onClick={() => onViewResults(test.campaign_id)}
              >
                View Results
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Results Display */}
      {abTestResults && (
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">
              A/B Test Results: {abTestResults.campaign_name}
            </h3>
            <Button size="sm" variant="ghost" onClick={onClearResults}>Close</Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium">Variant</th>
                  <th className="text-right p-3 text-xs font-medium">Discount</th>
                  <th className="text-right p-3 text-xs font-medium">Signups</th>
                  <th className="text-right p-3 text-xs font-medium">Redemptions</th>
                  <th className="text-right p-3 text-xs font-medium">Conversion</th>
                  <th className="text-right p-3 text-xs font-medium">Winner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {abTestResults.results?.map((result) => (
                  <tr key={result.variant_id} className={result.is_winner ? 'bg-green-500/5' : ''}>
                    <td className="p-3 font-medium">{result.variant_name}</td>
                    <td className="p-3 text-right">{result.discount_percent}%</td>
                    <td className="p-3 text-right">{result.signups}</td>
                    <td className="p-3 text-right">{result.redemptions}</td>
                    <td className="p-3 text-right font-bold">{result.conversion_rate}%</td>
                    <td className="p-3 text-right">
                      {result.is_winner && <Trophy className="w-4 h-4 text-yellow-500 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">{abTestResults.recommendation}</p>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={onSetShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5" />
              Create A/B Test Variant
            </DialogTitle>
            <DialogDescription>
              Test different discount percentages to see which converts better.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-1">
              <Label className="text-xs">Campaign ID</Label>
              <select
                value={newABTest.campaign_id}
                onChange={(e) => onSetNewABTest({...newABTest, campaign_id: parseInt(e.target.value)})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value={1}>Campaign 1 - Newsletter</option>
                <option value={2}>Campaign 2 - SMS Club</option>
                <option value={3}>Campaign 3 - Scratch & Win</option>
                <option value={4}>Campaign 4 - Biz Card</option>
              </select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Variant Name</Label>
              <Input
                placeholder="e.g., High Discount Test"
                value={newABTest.variant_name}
                onChange={(e) => onSetNewABTest({...newABTest, variant_name: e.target.value})}
                data-testid="ab-variant-name"
              />
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Discount Percentage (Max 15%)</Label>
              <select
                value={newABTest.discount_percent}
                onChange={(e) => onSetNewABTest({...newABTest, discount_percent: parseInt(e.target.value)})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {DISCOUNT_OPTIONS.map(v => (
                  <option key={v} value={v}>{v}% off</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Description (optional)</Label>
              <Input
                placeholder="Notes about this test..."
                value={newABTest.description}
                onChange={(e) => onSetNewABTest({...newABTest, description: e.target.value})}
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button onClick={onCreateABTest} className="flex-1" data-testid="create-ab-submit">
                Create Variant
              </Button>
              <Button variant="outline" onClick={() => onSetShowCreateDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
